import { gql, useLazyQuery } from "@apollo/client";
import { captureException } from "@sentry/browser";
import { createContext, useEffect, useRef, useState } from "react";

import {
  getGpsCoords,
  type LocationType,
  regionByCoords,
} from "@/helpers/location";
import useAnalytics from "@/hooks/use-analytics";

interface LocationContextStateType {
  apiLoading: boolean;
  apiLocation: ApiLocationType | undefined;
  gpsLoading: boolean;
  gpsLocation: LocationType | undefined;
}

const API_LOCATION_STORAGE_KEY = "ip_location";
const GPS_LOCATION_STORAGE_KEY = "gps_location";

const GEO_QUERY = gql`
  {
    geoipLookup {
      city
      country
      lat
      lng
      region
    }
  }
`;

interface ApiLocationType {
  city: string;
  country: string;
  lat: number;
  lng: number;
  region: string;
}

interface GeoLookupDataType {
  geoipLookup: ApiLocationType;
}

const INITIAL_STATE = {
  apiLoading: true,
  apiLocation: undefined,
  gpsLoading: true,
  gpsLocation: undefined,
};

type LocationContextType = [
  LocationContextStateType,
  React.Dispatch<React.SetStateAction<LocationContextStateType>>,
];

export const LocationContext = createContext<LocationContextType>([
  INITIAL_STATE,
  () => null,
]);

interface LocationProviderProps {
  children: React.ReactNode;
}

export default function LocationProvider({ children }: LocationProviderProps) {
  const [state, setState] = useState<LocationContextStateType>(INITIAL_STATE);

  const { track } = useAnalytics();
  const trackRef = useRef(track);
  trackRef.current = track;
  const [fetchApi] = useLazyQuery<GeoLookupDataType>(GEO_QUERY);

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetch api shouldnt rerun effect
  useEffect(() => {
    const stored = sessionStorage.getItem(API_LOCATION_STORAGE_KEY);

    if (stored) {
      setState((oldState) => ({
        ...oldState,
        apiLoading: false,
        apiLocation: JSON.parse(stored),
      }));
      return;
    }

    (async () => {
      try {
        const { data } = await fetchApi();
        if (data?.geoipLookup) {
          setState((oldState) => ({
            ...oldState,
            apiLocation: data.geoipLookup,
          }));
          sessionStorage.setItem(
            API_LOCATION_STORAGE_KEY,
            JSON.stringify(data.geoipLookup)
          );
        }
      } catch (error) {
        captureException(error);
      }

      setState((oldState) => ({
        ...oldState,
        apiLoading: false,
      }));
    })();
  }, []);

  useEffect(() => {
    const stored = sessionStorage.getItem(GPS_LOCATION_STORAGE_KEY);

    if (stored) {
      setState((oldState) => ({
        ...oldState,
        gpsLoading: false,
        gpsLocation: JSON.parse(stored),
      }));
      return;
    }

    if ("permissions" in navigator) {
      (async () => {
        const { state } = await navigator.permissions.query({
          name: "geolocation",
        });

        if (state === "granted") {
          try {
            const { lat, lng } = await getGpsCoords();
            const gpsLocation = {
              address: await regionByCoords(`${lat},${lng}`),
              location: `${lat},${lng}`,
            };

            trackRef.current("User Location Updated", { lat, lng });
            setState((oldState) => ({ ...oldState, gpsLocation }));
            sessionStorage.setItem(
              GPS_LOCATION_STORAGE_KEY,
              JSON.stringify(gpsLocation)
            );
          } catch (error) {
            captureException(error);
          }
        }

        setState((oldState) => ({ ...oldState, gpsLoading: false }));

        if (state === "denied") {
          trackRef.current("Location Sharing Skipped");
        }
      })();
    }
  }, []);

  return (
    <LocationContext.Provider value={[state, setState]}>
      {children}
    </LocationContext.Provider>
  );
}
